import { t, Trans } from '@lingui/macro'
import { AspenGreenlightSyncConfigService, GreenlightSiteDTO, GreenlightSyncConfigDTO } from '@om1/aspen-api'
import { Autocomplete, Button, Chip, CircularProgress, Switch, TextField, Typography } from '@mui/material'
import { Ban, ToggleRight } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

/**
 * Admin page (admin:aspen) for the current org's Greenlight push gate. Per-org /
 * RLS-scoped: the caller only ever sees their own org.
 *
 * Block-centric model. Resolution is exact `(org, site)` → study-level
 * `(org, null)` → off, so:
 *  - the study-level row is the org-wide master switch;
 *  - a blocked site is a `(org, site_id, enabled=false)` row that wins over the
 *    master for patients with that exact site_id. Unblocking deletes the row.
 * The UI only ever creates disabled site rows, so "site row" ≡ "blocked site".
 */
const studyLevelRow = (rows: GreenlightSyncConfigDTO[]): GreenlightSyncConfigDTO | null => rows.find((row) => row.siteId == null) ?? null

const blockedSiteRows = (rows: GreenlightSyncConfigDTO[]): GreenlightSyncConfigDTO[] =>
    rows.filter((row) => row.siteId != null && !row.greenlightEnabled).sort((a, b) => (a.siteId ?? '').localeCompare(b.siteId ?? ''))

/**
 * Surface the API's own message (FastAPI `detail`, incl. the global 409
 * "Unique constraint violation") instead of a flat generic string, so a
 * conflict/permission error is actionable rather than swallowed.
 */
const apiErrorMessage = (err: unknown, fallback: string): string => {
    const detail = (err as { body?: { detail?: unknown } })?.body?.detail
    if (Array.isArray(detail) && detail[0] && typeof detail[0].msg === 'string') return detail[0].msg
    if (typeof detail === 'string') return detail
    return fallback
}

const GreenlightConfigPage: React.FC = () => {
    const [rows, setRows] = useState<GreenlightSyncConfigDTO[]>([])
    const [availableSites, setAvailableSites] = useState<GreenlightSiteDTO[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [saving, setSaving] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedSites, setSelectedSites] = useState<string[]>([])

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await AspenGreenlightSyncConfigService.indexGreenlightSyncConfigGet({
                page: 1,
                limit: 100
            })
            setRows(response.data)
        } catch (err) {
            setError(apiErrorMessage(err, t`Failed to load Greenlight configuration`))
        } finally {
            setLoading(false)
        }
        // Picker options are best-effort: if this fails the multi-select still
        // works as free text, so don't fail the whole page on it.
        try {
            setAvailableSites(await AspenGreenlightSyncConfigService.sitesGreenlightSyncConfigSitesGet())
        } catch {
            setAvailableSites([])
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    // Run a mutation, then reload so the list reflects server state. Errors are
    // surfaced (incl. 409 on a duplicate site) rather than swallowed. Returns
    // whether it succeeded so callers can, e.g., only clear a form on success.
    const mutate = async (op: () => Promise<unknown>, fallbackError: string): Promise<boolean> => {
        setSaving(true)
        setError(null)
        try {
            await op()
            await load()
            return true
        } catch (err) {
            setError(apiErrorMessage(err, fallbackError))
            return false
        } finally {
            setSaving(false)
        }
    }

    const study = studyLevelRow(rows)
    const studyEnabled = !!study?.greenlightEnabled
    const blocked = blockedSiteRows(rows)
    // Never offer a site that already has a config row (a second POST would 409).
    const configuredSiteIds = new Set(rows.filter((row) => row.siteId != null).map((row) => row.siteId))
    const siteMeta = new Map(availableSites.map((site) => [site.siteId, site]))
    const siteOptions = availableSites.map((site) => site.siteId).filter((id) => !configuredSiteIds.has(id))
    const siteOptionLabel = (id: string): string => {
        const meta = siteMeta.get(id)
        if (!meta) return id
        return `${meta.siteName ? `${id} — ${meta.siteName}` : id} (${meta.patientCount})`
    }

    const toggleStudy = (enabled: boolean) => {
        if (study) {
            return mutate(
                () =>
                    AspenGreenlightSyncConfigService.patchGreenlightSyncConfigConfigIdPatch({
                        configId: study.id,
                        requestBody: { id: study.id, greenlightEnabled: enabled }
                    }),
                t`Failed to update Greenlight configuration`
            )
        }
        // No study-level row yet: turning it on creates one (turning "off" with no
        // row is already the effective state, so it's a no-op).
        if (enabled) {
            return mutate(
                () => AspenGreenlightSyncConfigService.createGreenlightSyncConfigPost({ requestBody: { greenlightEnabled: true } }),
                t`Failed to enable Greenlight`
            )
        }
    }

    const blockSites = async () => {
        if (selectedSites.length === 0) return
        setSaving(true)
        setError(null)
        // Each site is an independent POST; some can commit while others 409.
        // Use allSettled (not Promise.all, which would abandon the reload after
        // the first rejection and hide the sites that already got blocked).
        const results = await Promise.allSettled(
            selectedSites.map((siteId) =>
                AspenGreenlightSyncConfigService.createGreenlightSyncConfigPost({
                    requestBody: { siteId, greenlightEnabled: false }
                })
            )
        )
        await load() // reflect whatever committed, regardless of partial failure
        const failedSites = selectedSites.filter((_, index) => results[index].status === 'rejected')
        if (failedSites.length > 0) {
            const rejection = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
            setError(apiErrorMessage(rejection?.reason, t`Failed to block some sites`))
        }
        // Keep only the sites that failed so the admin can retry them.
        setSelectedSites(failedSites)
        setSaving(false)
    }

    const unblock = (row: GreenlightSyncConfigDTO) =>
        mutate(() => AspenGreenlightSyncConfigService.deleteGreenlightSyncConfigConfigIdDelete({ configId: row.id }), t`Failed to unblock site`)

    return (
        <div className='px-8 py-6'>
            <div className='max-w-3xl'>
                {/* Header — matches the app's icon-badge + title pattern. */}
                <div className='mb-6 flex items-center'>
                    <div className='mr-3 flex-shrink-0 rounded-full bg-primary-main p-1.5 text-white'>
                        <ToggleRight size={20} />
                    </div>
                    <div>
                        <Typography variant='h5' className='font-bold text-gray-800'>
                            <Trans>Greenlight Configuration</Trans>
                        </Typography>
                        <div className='mt-1 text-sm text-gray-500'>
                            <Trans>Controls whether newly created patients in this study are pushed to Greenlight.</Trans>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className='flex justify-center py-16'>
                        <CircularProgress />
                    </div>
                ) : (
                    <div className='flex flex-col gap-4'>
                        {/* Study master switch */}
                        <section className='rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
                            <div className='flex items-start justify-between gap-4'>
                                <div>
                                    <div className='flex items-center'>
                                        <Typography variant='subtitle1' className='font-semibold text-gray-800'>
                                            <Trans>Study Greenlight</Trans>
                                        </Typography>
                                        <span
                                            className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                studyEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                            }`}
                                        >
                                            {studyEnabled ? <Trans>On</Trans> : <Trans>Off</Trans>}
                                        </span>
                                    </div>
                                    <div className='mt-1 text-sm text-gray-500'>
                                        <Trans>When on, new patients in this study are pushed to Greenlight — except any sites blocked below.</Trans>
                                    </div>
                                </div>
                                <Switch
                                    checked={studyEnabled}
                                    disabled={saving}
                                    onChange={(event) => toggleStudy(event.target.checked)}
                                    inputProps={{ 'aria-label': t`Study Greenlight` }}
                                />
                            </div>
                        </section>

                        {/* Site blocks */}
                        <section className='rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
                            <Typography variant='subtitle1' className='font-semibold text-gray-800'>
                                <Trans>Block specific sites</Trans>
                            </Typography>
                            <div className='mt-1 mb-3 text-sm text-gray-500'>
                                <Trans>Blocked sites are never pushed to Greenlight, even while the study is on.</Trans>
                            </div>

                            <div className='flex items-start gap-3'>
                                <Autocomplete
                                    multiple
                                    options={siteOptions}
                                    value={selectedSites}
                                    onChange={(_event, value) => setSelectedSites(value)}
                                    getOptionLabel={(option) => siteOptionLabel(option)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option}>
                                            {siteOptionLabel(option)}
                                        </li>
                                    )}
                                    noOptionsText={t`No sites available`}
                                    className='flex-1'
                                    renderInput={(params) => <TextField {...params} size='small' label={t`Sites`} placeholder={t`Select sites`} />}
                                />
                                <Button
                                    variant='contained'
                                    color='error'
                                    startIcon={<Ban size={16} />}
                                    disabled={saving || selectedSites.length === 0}
                                    onClick={blockSites}
                                    className='whitespace-nowrap'
                                >
                                    <Trans>Block Greenlight</Trans>
                                </Button>
                            </div>

                            <div className='mt-5'>
                                <div className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                                    <Trans>Blocked sites</Trans>
                                </div>
                                {blocked.length === 0 ? (
                                    <div className='rounded-md border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-400'>
                                        <Trans>No sites are blocked — every site follows the study switch.</Trans>
                                    </div>
                                ) : (
                                    <div className='flex flex-wrap gap-2'>
                                        {blocked.map((row) => (
                                            <Chip
                                                key={row.id}
                                                label={row.siteId}
                                                color='error'
                                                variant='outlined'
                                                disabled={saving}
                                                onDelete={() => unblock(row)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                )}

                {error && (
                    <Typography color='error' className='mt-3'>
                        {error}
                    </Typography>
                )}
            </div>
        </div>
    )
}

export default GreenlightConfigPage
